import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faTrash,
  faBuilding,
  faMapMarkerAlt,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const PropertyGroupEdit = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'tower',
    location: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        setLoading(true);
        const groupData = await propertyService.getPropertyGroupById(groupId);
        setGroup(groupData);
        setFormData({
          name: groupData.name || '',
          type: groupData.type || 'tower',
          location: groupData.location || ''
        });
      } catch (error) {
        console.error('Error fetching group:', error);
        setError('Failed to load property group: ' + (error.message || error));
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchGroup();
    }
  }, [groupId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.location.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await propertyService.updatePropertyGroup(groupId, {
        name: formData.name.trim(),
        type: formData.type,
        location: formData.location.trim()
      });

      setSuccess('Property group updated successfully!');
      
      // Redirect back to group details after a short delay
      setTimeout(() => {
        navigate(`/manager/property-groups/${groupId}`);
      }, 1500);

    } catch (error) {
      console.error('Error updating group:', error);
      setError('Failed to update property group: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this property group? This will also delete all units in this group.')) {
      try {
        setSaving(true);
        await propertyService.deletePropertyGroup(groupId);
        navigate('/dashboard');
      } catch (error) {
        console.error('Error deleting group:', error);
        setError('Failed to delete property group: ' + (error.message || error));
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          {error}
          <Button 
            variant="outline-danger" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Property group not found
          <Button 
            variant="outline-warning" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/manager/property-groups/${groupId}`)}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Group
          </Button>
          <div>
            <h2 className="mb-1">Edit Property Group</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faBuilding} className="me-1" />
              {group.name}
            </p>
          </div>
        </div>
        <Button variant="outline-danger" onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
          Logout
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
                Property Group Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                        Group Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter group name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group Type <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="tower">Tower</option>
                        <option value="community">Community</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                    Location <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter location/address"
                    required
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline-secondary"
                    onClick={() => navigate(`/manager/property-groups/${groupId}`)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete Group
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PropertyGroupEdit; 